import React, { useState, useEffect } from 'react';
import { apiClient } from '../../App';
import Layout from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, Clock, Mail, CheckCircle, XCircle, Loader2, 
  Plus, Search, Phone, Calendar, Trophy, ArrowUpRight,
  RefreshCw, UserPlus
} from 'lucide-react';

const AdminProspects = () => {
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, active, expired, converted
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [extendDays, setExtendDays] = useState(7);
  const [actionLoading, setActionLoading] = useState({});
  
  // Create trial user state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTrialUser, setNewTrialUser] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    force_reset: false
  });

  useEffect(() => {
    fetchProspects();
  }, []);

  const fetchProspects = async () => {
    try {
      const res = await apiClient.get('/admin/prospects');
      setProspects(res.data);
    } catch (err) {
      toast.error('Failed to fetch prospects');
    } finally {
      setLoading(false);
    }
  };

  const handleExtendTrial = async () => {
    if (!selectedProspect) return;
    
    setActionLoading(prev => ({ ...prev, [selectedProspect.id]: true }));
    try {
      await apiClient.put(`/admin/prospects/${selectedProspect.id}/extend`, {
        additional_days: extendDays
      });
      toast.success(`Trial extended by ${extendDays} days`);
      fetchProspects();
      setExtendDialogOpen(false);
    } catch (err) {
      toast.error('Failed to extend trial');
    } finally {
      setActionLoading(prev => ({ ...prev, [selectedProspect.id]: false }));
    }
  };

  const handleSendFollowup = async (prospect) => {
    setActionLoading(prev => ({ ...prev, [`followup-${prospect.id}`]: true }));
    try {
      await apiClient.post(`/admin/prospects/${prospect.id}/send-followup`);
      toast.success(`Follow-up email sent to ${prospect.email}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send follow-up');
    } finally {
      setActionLoading(prev => ({ ...prev, [`followup-${prospect.id}`]: false }));
    }
  };

  const handleConvert = async (prospect) => {
    setActionLoading(prev => ({ ...prev, [`convert-${prospect.id}`]: true }));
    try {
      await apiClient.post(`/admin/prospects/${prospect.id}/convert`);
      toast.success(`${prospect.name} converted to paid user`);
      fetchProspects();
    } catch (err) {
      toast.error('Failed to convert user');
    } finally {
      setActionLoading(prev => ({ ...prev, [`convert-${prospect.id}`]: false }));
    }
  };

  const handleCreateTrialUser = async (e) => {
    e.preventDefault();
    
    if (!newTrialUser.name || !newTrialUser.email || !newTrialUser.password) {
      toast.error('Please fill in name, email, and password');
      return;
    }
    
    setCreating(true);
    try {
      const res = await apiClient.post('/admin/prospects/create', newTrialUser);
      toast.success(res.data.message);
      setCreateDialogOpen(false);
      setNewTrialUser({ name: '', email: '', password: '', phone: '', force_reset: false });
      fetchProspects();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create trial user');
    } finally {
      setCreating(false);
    }
  };

  const filteredProspects = prospects.filter(p => {
    const matchesSearch = 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.phone && p.phone.includes(searchTerm));
    
    if (!matchesSearch) return false;
    
    switch (filter) {
      case 'active':
        return !p.is_expired && !p.converted_to_paid;
      case 'expired':
        return p.is_expired && !p.converted_to_paid;
      case 'converted':
        return p.converted_to_paid;
      default:
        return true;
    }
  });

  const stats = {
    total: prospects.length,
    active: prospects.filter(p => !p.is_expired && !p.converted_to_paid).length,
    expired: prospects.filter(p => p.is_expired && !p.converted_to_paid).length,
    converted: prospects.filter(p => p.converted_to_paid).length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#EFB342]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-[#09090b] p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">Prospects</h1>
              <p className="text-stone-500 text-sm mt-1">Manage trial users and follow up with potential customers</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                data-testid="create-trial-user-btn"
              >
                <UserPlus size={16} className="mr-2" />
                Create Trial User
              </Button>
              <Button
                onClick={fetchProspects}
                variant="outline"
                className="border-stone-300 text-stone-700"
              >
                <RefreshCw size={16} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div 
              className={`bg-white border rounded-xl p-4 cursor-pointer transition-colors ${filter === 'all' ? 'border-[#EFB342]' : 'border-stone-200 hover:border-stone-300'}`}
              onClick={() => setFilter('all')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-stone-100 rounded-lg flex items-center justify-center">
                  <Users className="text-stone-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.total}</p>
                  <p className="text-xs text-stone-500">Total Prospects</p>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white border rounded-xl p-4 cursor-pointer transition-colors ${filter === 'active' ? 'border-green-500' : 'border-stone-200 hover:border-stone-300'}`}
              onClick={() => setFilter('active')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Clock className="text-green-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.active}</p>
                  <p className="text-xs text-stone-500">Active Trials</p>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white border rounded-xl p-4 cursor-pointer transition-colors ${filter === 'expired' ? 'border-red-500' : 'border-stone-200 hover:border-stone-300'}`}
              onClick={() => setFilter('expired')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center">
                  <XCircle className="text-red-400" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.expired}</p>
                  <p className="text-xs text-stone-500">Expired (No Convert)</p>
                </div>
              </div>
            </div>

            <div 
              className={`bg-white border rounded-xl p-4 cursor-pointer transition-colors ${filter === 'converted' ? 'border-[#EFB342]' : 'border-stone-200 hover:border-stone-300'}`}
              onClick={() => setFilter('converted')}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#EFB342]/20 rounded-lg flex items-center justify-center">
                  <CheckCircle className="text-[#EFB342]" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-stone-900">{stats.converted}</p>
                  <p className="text-xs text-stone-500">Converted</p>
                </div>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" size={18} />
            <Input
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white border-stone-200"
            />
          </div>

          {/* Prospects List */}
          <div className="space-y-3">
            {filteredProspects.length === 0 ? (
              <div className="text-center py-12 text-stone-500">
                {searchTerm || filter !== 'all' ? 'No prospects match your search/filter' : 'No trial users yet'}
              </div>
            ) : (
              filteredProspects.map((prospect) => (
                <div
                  key={prospect.id}
                  className={`bg-white border rounded-xl p-4 sm:p-5 ${
                    prospect.converted_to_paid 
                      ? 'border-green-800/50' 
                      : prospect.is_expired 
                        ? 'border-red-800/50' 
                        : 'border-stone-200'
                  }`}
                  data-testid={`prospect-${prospect.id}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    {/* User Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-stone-900 truncate">{prospect.name}</h3>
                        {prospect.converted_to_paid ? (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            Converted
                          </span>
                        ) : prospect.is_expired ? (
                          <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                            Expired
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-[#EFB342]/20 text-[#EFB342] text-xs rounded-full">
                            {prospect.days_remaining} days left
                          </span>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600">
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {prospect.email}
                        </span>
                        {prospect.phone && (
                          <span className="flex items-center gap-1">
                            <Phone size={14} />
                            {prospect.phone}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          Started: {formatDate(prospect.trial_start_date)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Trophy size={14} />
                          {prospect.total_points} pts
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {!prospect.converted_to_paid && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-stone-300 text-stone-700"
                            onClick={() => {
                              setSelectedProspect(prospect);
                              setExtendDays(7);
                              setExtendDialogOpen(true);
                            }}
                          >
                            <Plus size={14} className="mr-1" />
                            Extend
                          </Button>
                          
                          {prospect.is_expired && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-stone-300 text-stone-700"
                              onClick={() => handleSendFollowup(prospect)}
                              disabled={actionLoading[`followup-${prospect.id}`]}
                            >
                              {actionLoading[`followup-${prospect.id}`] ? (
                                <Loader2 size={14} className="mr-1 animate-spin" />
                              ) : (
                                <Mail size={14} className="mr-1" />
                              )}
                              Follow-up
                            </Button>
                          )}
                          
                          <Button
                            size="sm"
                            className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                            onClick={() => handleConvert(prospect)}
                            disabled={actionLoading[`convert-${prospect.id}`]}
                          >
                            {actionLoading[`convert-${prospect.id}`] ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <ArrowUpRight size={14} className="mr-1" />
                            )}
                            Convert to Paid
                          </Button>
                        </>
                      )}
                      
                      {prospect.converted_to_paid && (
                        <span className="text-sm text-green-400 flex items-center gap-1">
                          <CheckCircle size={14} />
                          Converted {formatDate(prospect.converted_at)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Extend Trial Dialog */}
      <Dialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader>
            <DialogTitle className="text-stone-900">Extend Trial Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-stone-600 text-sm">
              Extend the trial period for <strong className="text-stone-900">{selectedProspect?.name}</strong>
            </p>
            <div>
              <label className="text-sm text-stone-600 mb-1 block">Additional Days</label>
              <Input
                type="number"
                value={extendDays}
                onChange={(e) => setExtendDays(parseInt(e.target.value) || 0)}
                min={1}
                max={30}
                className="bg-stone-50 border-stone-200"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setExtendDialogOpen(false)}
                className="border-stone-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleExtendTrial}
                className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                disabled={actionLoading[selectedProspect?.id]}
              >
                {actionLoading[selectedProspect?.id] ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : null}
                Extend Trial
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Trial User Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-white border-stone-200">
          <DialogHeader>
            <DialogTitle className="text-stone-900 flex items-center gap-2">
              <UserPlus size={20} className="text-[#EFB342]" />
              Create Trial User
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateTrialUser} className="space-y-4 pt-4">
            <p className="text-stone-600 text-sm bg-stone-50 p-3 rounded-lg">
              <strong className="text-[#EFB342]">Admin privilege:</strong> This bypasses the one-time trial limit, allowing you to create trial accounts for testing or special cases.
            </p>
            <div>
              <Label className="text-stone-700">Name *</Label>
              <Input
                type="text"
                value={newTrialUser.name}
                onChange={(e) => setNewTrialUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                className="bg-stone-50 border-stone-200 mt-1"
                data-testid="new-trial-name"
              />
            </div>
            <div>
              <Label className="text-stone-700">Email *</Label>
              <Input
                type="email"
                value={newTrialUser.email}
                onChange={(e) => setNewTrialUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                className="bg-stone-50 border-stone-200 mt-1"
                data-testid="new-trial-email"
              />
            </div>
            <div>
              <Label className="text-stone-700">Password *</Label>
              <Input
                type="password"
                value={newTrialUser.password}
                onChange={(e) => setNewTrialUser(prev => ({ ...prev, password: e.target.value }))}
                placeholder="••••••••"
                className="bg-stone-50 border-stone-200 mt-1"
                data-testid="new-trial-password"
              />
            </div>
            <div>
              <Label className="text-stone-700">Phone (optional)</Label>
              <Input
                type="tel"
                value={newTrialUser.phone}
                onChange={(e) => setNewTrialUser(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="555-123-4567"
                className="bg-stone-50 border-stone-200 mt-1"
                data-testid="new-trial-phone"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg border border-stone-300">
              <input
                type="checkbox"
                id="force-reset"
                checked={newTrialUser.force_reset}
                onChange={(e) => setNewTrialUser(prev => ({ ...prev, force_reset: e.target.checked }))}
                className="w-4 h-4 rounded border-stone-300 bg-stone-100 text-[#EFB342] focus:ring-[#EFB342]"
                data-testid="force-reset-checkbox"
              />
              <label htmlFor="force-reset" className="text-sm text-stone-700 cursor-pointer">
                <span className="font-medium text-[#EFB342]">Reset existing user's trial</span>
                <p className="text-xs text-stone-500 mt-0.5">
                  If this email already exists, reset their trial to a fresh 7 days (keeps their data/points)
                </p>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateDialogOpen(false);
                  setNewTrialUser({ name: '', email: '', password: '', phone: '', force_reset: false });
                }}
                className="border-stone-300"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[#EFB342] text-black hover:bg-[#D99F30]"
                disabled={creating}
                data-testid="submit-create-trial"
              >
                {creating ? (
                  <Loader2 size={16} className="mr-2 animate-spin" />
                ) : (
                  <UserPlus size={16} className="mr-2" />
                )}
                Create Trial User
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminProspects;
